import React from 'react';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { ServerUrls } from '~commonInfra/url/ServerUrls';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { ServerTasksSubspaceName } from '~src/spacesPlatform/state/SpaceInfo';

import { RouterError } from '../../../bootstrap/routerHelpers/RouterError';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../../sessionManagement/SessionManagementService';
import { TasksSpaceProvider, TasksSpaceProviderProps } from '../tasksSpace/TasksSpaceProvider';

export interface ServerTasksSpaceRouteParams {}

export interface ServerTasksRouteComponentProps extends RouteComponentProps<ServerTasksSpaceRouteParams> {}

export interface ServerTasksSpaceRouterOwnProps {
  urls: SpacesSiteUrls;
  readonly provider: (subspaceName: ServerTasksSubspaceName, providerProps: ServerTasksSpaceComponentWithRouterProps) => (props: ServerTasksRouteComponentProps) => JSX.Element;
}

export type ServerTasksSpaceRouterProps = ServerTasksSpaceRouterOwnProps;

export class ServerTasksSpaceRouterComponent extends React.Component<ServerTasksSpaceRouterProps> {
  private server: SessionManagementService;

  public constructor(props: ServerTasksSpaceRouterProps) {
    super(props);
    this.server = getSessionManagementServiceInstance();
  }

  public render() {
    return (
      <Switch>
        <Route exact path={ServerUrls.getInstance().tasksExtractRefreshesPath()} render={this.props.provider('extractRefreshes', this.props)}/>
        {this.protectedRouteForFlowsSubspace(this.server)}
        <Route exact path={ServerUrls.getInstance().tasksSubscriptionsPath()} render={this.props.provider('subscriptions', this.props)}/>
        {this.protectedRouteForDataAccelerationSubspace(this.server)}
        {this.protectedRouteForLinkedTasksSubspace(this.server)}

        <Route exact path={ServerUrls.getInstance().tasksPath()} render={() => (
          <Redirect to={ServerUrls.getInstance().tasksExtractRefreshesPath()} />
        )} />
        <Route render={this.error}/>
      </Switch>
    );
  }

  private error = (_props: RouteComponentProps): JSX.Element => {
    return <RouterError errorType={'default'} urls={this.props.urls}/>;
  };

  private protectedRouteForFlowsSubspace(server: SessionManagementService) {
    return server.flowsOnServerEnabled() &&
      <Route
        exact path={ServerUrls.getInstance().tasksFlowsPath()}
        render={this.props.provider('runFlowTasks', this.props)}
      />;
  }

  private protectedRouteForDataAccelerationSubspace(server: SessionManagementService) {
    return FeatureFlags.accelerationSiteAdminUIEnabled &&
      <Route
        exact path={ServerUrls.getInstance().tasksDataAccelerationPath()}
        render={this.props.provider('dataAcceleration', this.props)}
      />;
  }

  private protectedRouteForLinkedTasksSubspace(server: SessionManagementService) {
    return (
      <Route
        exact path={ServerUrls.getInstance().tasksLinkedTasksPath()}
        render={this.props.provider('linkedTasks', this.props)}
      />);
  }
}

export type ServerTasksSpaceComponentWithRouterProps = Omit<ServerTasksSpaceRouterProps, 'provider'>;
const provider = (subspaceName: ServerTasksSubspaceName, providerProps: ServerTasksSpaceComponentWithRouterProps): (props: ServerTasksRouteComponentProps) => JSX.Element => {
  return (props: ServerTasksRouteComponentProps): JSX.Element => {
    const spaceProps: TasksSpaceProviderProps = {
      routeLocationSearch: props.location.search,
      routeLocationPath: props.location.pathname,
      siteName: undefined,
      subspaceName,
      urls: providerProps.urls,
      inServerArea: true
    };
    return <TasksSpaceProvider {...spaceProps} />;
  };
};

export type ServerTasksSpaceComponentOwnProps = Omit<ServerTasksSpaceRouterOwnProps, 'provider'>;
const ServerTasksSpaceComponent = (props: ServerTasksSpaceComponentWithRouterProps) => {
  return (
    <ServerTasksSpaceRouterComponent {...props} provider={provider}/>
  );
};

export const ServerTasksSpaceRouter: React.FunctionComponent<ServerTasksSpaceComponentOwnProps> = ServerTasksSpaceComponent;
